"use client";

import { useEffect, useState } from "react";
import { Text } from "@astryxdesign/core/Text";
import {
  getLandingSemanticColors,
  type LandingPageTheme,
} from "@/lib/landing/puck/themes";

type LandingThemePreviewProps = {
  theme: LandingPageTheme;
  previewMode?: "light" | "dark";
  compact?: boolean;
  className?: string;
};

function Swatch({
  label,
  color,
}: {
  label: string;
  color: string;
}) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
      <div
        className="h-8 rounded border border-black/10"
        style={{ background: color }}
        title={color}
      />
      <span className="truncate text-[10px] leading-tight text-secondary">
        {label}
      </span>
    </div>
  );
}

function MiniPageMock({
  colors,
  modeLabel,
}: {
  colors: ReturnType<typeof getLandingSemanticColors>;
  modeLabel: string;
}) {
  return (
    <div
      className="overflow-hidden rounded-lg border"
      style={{
        background: colors.pageBg,
        borderColor: colors.border,
        color: colors.textPrimary,
      }}
    >
      <div
        className="flex items-center justify-between px-3 py-1.5 text-[10px]"
        style={{
          borderBottom: `1px solid ${colors.border}`,
          color: colors.textSecondary,
        }}
      >
        <span>Превью: {modeLabel}</span>
      </div>
      <div className="space-y-2 p-3">
        <span
          className="inline-block rounded-full px-2 py-0.5 text-[10px] font-medium"
          style={{ background: colors.badgeBg, color: colors.badgeText }}
        >
          Бейдж
        </span>
        <p className="text-sm font-semibold leading-tight">Заголовок</p>
        <p
          className="text-[11px] leading-snug"
          style={{ color: colors.textSecondary }}
        >
          Подзаголовок
        </p>
        <div className="flex flex-wrap gap-1.5">
          <span
            className="rounded px-2 py-1 text-[10px] font-medium"
            style={{
              background: colors.buttonPrimaryBg,
              color: colors.buttonPrimaryText,
            }}
          >
            Кнопка
          </span>
          <span
            className="rounded border px-2 py-1 text-[10px]"
            style={{
              borderColor: colors.buttonSecondaryBorder,
              color: colors.buttonSecondaryText,
            }}
          >
            Вторичная
          </span>
        </div>
        <div
          className="relative rounded-md border p-2"
          style={{
            background: colors.cardBg,
            borderColor: colors.border,
          }}
        >
          <p className="text-[10px] font-medium">Карточка</p>
          <div
            className="absolute -right-1 -top-1 rounded border px-1.5 py-0.5 text-[9px] shadow-sm"
            style={{
              background: colors.popupBg,
              borderColor: colors.popupBorder,
              color: colors.textPrimary,
            }}
          >
            Попап
          </div>
        </div>
      </div>
    </div>
  );
}

export function LandingThemePreview({
  theme,
  previewMode = "light",
  compact = false,
  className = "",
}: LandingThemePreviewProps) {
  const colors = getLandingSemanticColors(theme, { previewMode });
  const modeLabel = previewMode === "dark" ? "тёмная" : "светлая";

  if (compact) {
    return (
      <div className={className}>
        <MiniPageMock colors={colors} modeLabel={modeLabel} />
      </div>
    );
  }

  return (
    <div className={`flex w-full flex-col gap-3 ${className}`}>
      <MiniPageMock colors={colors} modeLabel={modeLabel} />
      <div className="grid grid-cols-4 gap-2 sm:grid-cols-5">
        <Swatch label="Фон" color={colors.pageBg} />
        <Swatch label="Секция" color={colors.sectionBg} />
        <Swatch label="Карточка" color={colors.cardBg} />
        <Swatch label="Текст" color={colors.textPrimary} />
        <Swatch label="Подпись" color={colors.textSecondary} />
        <Swatch label="Кнопка" color={colors.buttonPrimaryBg} />
        <Swatch label="Текст кн." color={colors.buttonPrimaryText} />
        <Swatch label="Бейдж" color={colors.badgeBg} />
        <Swatch label="Рамка" color={colors.border} />
        <Swatch label="Попап" color={colors.popupBg} />
      </div>
    </div>
  );
}
